// 樹上〔⋯〕›〔加到檢視…〕/〔加到群組…〕：挑一個我能編輯的檢視（可選資料夾）或群組，把這一項加進去（整份替換、帶版本）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { isOrgAdmin } from '../../lib/planeApi';
import { meTreeApi, type DeviceGroup, type MeTreeItem, type View, type ViewItem } from '../../lib/orgTreeApi';
import { useViewsAndGroups } from './hooks';
import { otErr } from './otErr';

export function refOf(it: Pick<MeTreeItem, 'kind' | 'id' | 'home_id' | 'device_ref' | 'delegation' | 'name'>): ViewItem {
  const kind = it.kind === 'home' ? 'home' : it.kind === 'device' ? 'device' : 'node';
  const ref = it.kind === 'device' ? `${it.home_id}/${it.device_ref}` : it.kind === 'home' ? (it.home_id ?? it.id) : it.id;
  return { kind, ref, name: it.name, ...(it.delegation ? { delegation_id: it.delegation.delegation_id, from_org_name: it.delegation.from_org_name } : {}) };
}

export function canEdit(x: View | DeviceGroup, orgs: { org_id: string; role: string }[]): boolean {
  if (x.can_edit !== undefined) return x.can_edit;
  if (x.owner_kind === 'user') return true;
  const o = orgs.find((y) => y.org_id === x.owner_id);
  return !!o && isOrgAdmin(o);
}

export function AddToDialog({ kind, item, close }: { kind: 'view' | 'group'; item: MeTreeItem & { _api: string }; close(): void }) {
  const { t } = useTranslation();
  const { planes, orgs } = useScope();
  const qc = useQueryClient();
  const { views, groups } = useViewsAndGroups(planes, orgs.filter((o) => o.kind === 'team'));
  const list = ((kind === 'view' ? views.data : groups.data) ?? []).filter((x) => canEdit(x, orgs));
  const [sel, setSel] = useState('');
  const [folder, setFolder] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const target = list.find((x) => ('view_id' in x ? x.view_id : x.group_id) === sel);
  const folders = target && 'view_id' in target ? target.items.filter((i) => i.kind === 'folder') : [];
  async function add() {
    if (!target) return;
    setBusy(true);
    setErr(null);
    try {
      const owner = target.owner_kind === 'user' ? ({ kind: 'user' } as const) : ({ kind: 'org', org_id: target.owner_id } as const);
      const items = [...target.items, { ...refOf(item), parent_item_id: folder || null }];
      const api = (target as { _api?: string })._api ?? item._api; // 寫回目標檢視 / 群組所在的 plane
      if ('view_id' in target) await meTreeApi.saveView({ api }, owner, { view_id: target.view_id, name: target.name, items, expected_version: target.version });
      else await meTreeApi.saveGroup({ api }, owner, { group_id: target.group_id, name: target.name, items, expected_version: target.version });
      void qc.invalidateQueries({ queryKey: ['me-tree'] });
      close();
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={kind === 'view' ? t('ot.add_to_view_title', { name: item.name }) : t('ot.add_to_group_title', { name: item.name })} onClose={close}>
      <div className="form">
        {list.length === 0 ? (
          <p className="muted">{kind === 'view' ? t('ot.no_editable_views') : t('ot.no_editable_groups')}</p>
        ) : (
          <select className="input" value={sel} onChange={(e) => (setSel(e.target.value), setFolder(''))} aria-label={kind === 'view' ? t('ot.tab_views') : t('ot.tab_groups')}>
            <option value="">—</option>
            {list.map((x) => {
              const id = 'view_id' in x ? x.view_id : x.group_id;
              return (
                <option key={id} value={id}>
                  {x.name}
                  {x.owner_kind === 'org' ? ` (${x.owner_name ?? t('ot.org_owned')})` : ` (${t('ot.personal')})`}
                </option>
              );
            })}
          </select>
        )}
        {folders.length > 0 && (
          <select className="input" value={folder} onChange={(e) => setFolder(e.target.value)} aria-label={t('ot.folder')}>
            <option value="">{t('ot.top_level')}</option>
            {folders.map((f) => (
              <option key={f.item_id} value={f.item_id}>
                📁 {f.name}
              </option>
            ))}
          </select>
        )}
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={!target || busy} onClick={() => void add()}>
            {t('ot.add')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
